import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFamilyContext } from '../contexts/FamilyContext';
import { authService } from '../lib/auth-service';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';

export default function Auth() {
  const { signIn, error } = useFamilyContext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [authMode, setAuthMode] = useState('password'); // 'password', 'magic-link', 'reset-password'
  const [message, setMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const handlePasswordSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      await signIn(email, password);
    } catch (err) {
      console.error('Authentication error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      await authService.signInWithMagicLink(email);
      setShowSuccess(true);
      setMessage('Check your email for a magic link to sign in!');
    } catch (err) {
      console.error('Magic link error:', err);
      setMessage(err.message || 'Failed to send magic link');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      await authService.resetPassword(email);
      setShowSuccess(true);
      setMessage('Check your email for password reset instructions!');
    } catch (err) {
      console.error('Password reset error:', err);
      setMessage(err.message || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setMessage('');
    setShowSuccess(false);
    setEmail('');
    setPassword('');
  };

  const handleModeChange = (mode) => {
    setAuthMode(mode);
    resetForm();
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-azure-100 via-violet-50 to-pink-100">
        <div className="max-w-md w-full space-y-8 bg-neutral-50 p-8 rounded-xl shadow-lg">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <img 
                src="/papersedge-icon-transparent.png" 
                alt="Paper Sedge" 
                className="w-12 h-12"
              />
            </div>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-stone-900 mb-2">
              {authMode === 'magic-link' ? 'Magic Link Sent!' : 'Reset Link Sent!'}
            </h2>
            <p className="text-stone-600 mb-6">{message}</p>
            
            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowSuccess(false);
                  setAuthMode('password');
                  resetForm();
                }}
                className="w-full py-2 px-4 border border-stone-300 text-stone-700 rounded-md hover:bg-stone-50 transition-colors"
              >
                Back to Sign In
              </button>
              
              <p className="text-xs text-stone-500">
                Didn't receive the email? Check your spam folder or try again.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-azure-100 via-violet-50 to-pink-100">
      <div className="max-w-md w-full space-y-8 bg-neutral-50 p-8 rounded-xl shadow-lg">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <img 
              src="/papersedge-icon-transparent.png" 
              alt="Paper Sedge" 
              className="w-16 h-16"
            />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-stone-900">
            {authMode === 'password' && 'Sign in to your account'}
            {authMode === 'magic-link' && 'Sign in with Magic Link'}
            {authMode === 'reset-password' && 'Reset your password'}
          </h2>
          <p className="mt-2 text-sm text-stone-600">
            {authMode === 'password' && 'Welcome back to Paper Sedge'}
            {authMode === 'magic-link' && 'We\'ll send you a secure link to sign in'}
            {authMode === 'reset-password' && 'Enter your email to reset your password'}
          </p>
        </div>

        {/* Mode Navigation */}
        {authMode !== 'password' && (
          <div className="flex items-center justify-center">
            <button
              onClick={() => handleModeChange('password')}
              className="flex items-center gap-2 text-sm text-stone-600 hover:text-stone-800 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to password sign in
            </button>
          </div>
        )}

        <form 
          className="mt-8 space-y-6" 
          onSubmit={
            authMode === 'password' ? handlePasswordSignIn :
            authMode === 'magic-link' ? handleMagicLink :
            handlePasswordReset
          }
        >
          {(error || message) && (
            <div className={`rounded-md p-4 ${showSuccess ? 'bg-emerald-50' : 'bg-red-50'}`}>
              <p className={`text-sm ${showSuccess ? 'text-emerald-800' : 'text-red-800'}`}>
                {error || message}
              </p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-2">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-stone-900"
                placeholder="Enter your email"
              />
            </div>

            {authMode === 'password' && (
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-stone-700 mb-2">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-stone-900"
                  placeholder="Enter your password"
                />
              </div>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-neutral-50 bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Loading...' : 
                authMode === 'password' ? 'Sign in' :
                authMode === 'magic-link' ? 'Send Magic Link' :
                'Send Reset Link'
              }
            </button>
          </div>
        </form>

        {/* Authentication Options */}
        {authMode === 'password' && (
          <div className="space-y-4">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-stone-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-neutral-50 text-stone-500">Or sign in with</span>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <button
                onClick={() => handleModeChange('magic-link')}
                className="w-full inline-flex justify-center items-center py-2 px-4 border border-stone-300 rounded-lg bg-white text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors"
              >
                <Mail className="w-4 h-4 mr-2" />
                Magic Link
              </button>
            </div>

            <div className="text-center">
              <button
                onClick={() => handleModeChange('reset-password')}
                className="text-sm text-indigo-600 hover:text-indigo-500 transition-colors"
              >
                Forgot your password?
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 text-center">
          <div className="text-sm text-stone-600 mb-3">
            New to the family planner?
          </div>
          <Link
            to="/join-family"
            className="w-full inline-block py-2 px-4 bg-indigo-600 text-neutral-50 rounded-md hover:bg-indigo-700 transition-colors text-center text-sm font-medium"
          >
            Join with Invitation Code
          </Link>
        </div>
      </div>
    </div>
  );
}